import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Users as UsersIcon, VideoCamera } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";

const COVERS = [
  "https://images.pexels.com/photos/10744384/pexels-photo-10744384.jpeg",
  "https://images.pexels.com/photos/31468533/pexels-photo-31468533.jpeg",
  "https://images.pexels.com/photos/33865563/pexels-photo-33865563.jpeg",
];
const EMPTY = { title: "", description: "", code: "", filiere: "", category: "", cover_image: COVERS[0], meeting_url: "" };

export default function Courses() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const canCreate = user.role === "admin" || user.role === "professeur";

  const create = async () => {
    try {
      await api.post("/courses", form);
      toast.success("Cours créé");
      setOpen(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="courses-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Formations</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">
            {user.role === "etudiant" ? "Mes cours" : "Cours"}
          </h1>
        </div>
        {canCreate && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-primary hover:bg-primary/90" data-testid="create-course-btn">
                <Plus size={18} className="mr-2" /> Nouveau cours
              </Button>
            </DialogTrigger>
            <DialogContent className="max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Créer un cours</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="course-title" /></div>
                <div><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="MGT-101" /></div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="course-desc" /></div>
                <div><Label>Filière</Label><Input value={form.filiere} onChange={(e) => setForm({ ...form, filiere: e.target.value })} /></div>
                <div><Label>Lien visioconférence (optionnel)</Label><Input value={form.meeting_url} onChange={(e) => setForm({ ...form, meeting_url: e.target.value })} placeholder="https://meet.jit.si/..." data-testid="course-meeting" /></div>
                <div>
                  <Label>Image de couverture</Label>
                  <div className="mt-1 flex gap-2">
                    {COVERS.map((c) => (
                      <button key={c} onClick={() => setForm({ ...form, cover_image: c })} className={`h-14 w-20 overflow-hidden rounded border-2 ${form.cover_image === c ? "border-primary" : "border-transparent"}`}>
                        <img src={c} alt="" className="h-full w-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} className="bg-primary hover:bg-primary/90" data-testid="save-course-btn">Créer</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {courses.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-16 text-center text-muted-foreground">
          {user.role === "etudiant"
            ? "Aucun cours ne vous est attribué. Un professeur doit donner accès à votre groupe."
            : "Aucun cours pour le moment."}
        </div>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {courses.map((c, i) => (
            <motion.div
              key={c.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              onClick={() => navigate(`/app/cours/${c.id}`)}
              className="group cursor-pointer overflow-hidden rounded-xl border border-border bg-card transition-transform hover:-translate-y-1 hover:shadow-lg"
              data-testid={`course-card-${c.id}`}
            >
              <div className="relative h-36 overflow-hidden">
                <img src={c.cover_image || COVERS[0]} alt={c.title} className="h-full w-full object-cover transition-transform group-hover:scale-105" />
                <div className="absolute right-2 top-2 flex gap-1">
                  {c.meeting_url && <Badge className="bg-accent hover:bg-accent"><VideoCamera size={12} className="mr-1" weight="fill" /> Live</Badge>}
                  {c.published ? (
                    <Badge className="bg-emerald-600 hover:bg-emerald-600">Publié</Badge>
                  ) : (
                    <Badge variant="secondary">Brouillon</Badge>
                  )}
                </div>
              </div>
              <div className="p-5">
                {c.code && <p className="text-xs font-semibold uppercase tracking-wider text-accent">{c.code}</p>}
                <h3 className="mt-1 font-display text-lg font-bold leading-tight">{c.title}</h3>
                <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{c.description}</p>
                <div className="mt-4 flex items-center justify-between">
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <UsersIcon size={14} /> {c.students_count} étudiants
                  </span>
                  <span className="text-xs text-muted-foreground">{c.teacher_name}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
